import type { 
  Class,
  ClassCreateRequest, 
  ClassUpdateRequest,
  ClassSyncRequest,
  ClassSyncResponse,
  ClassImportRequest,
  ClassImportResponse,
  ApiResponse,
  ClassListParams, 
  ClassListResponse
} from '../../../shared/types';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API_BASE = '/api/v1/classes';

const classService = {
  listClasses,
  getClass,
  createClass,
  updateClass,
  deleteClass,
  syncClasses,
  importClasses,
  downloadClassTemplate,
  parseClassExcelData,
  enrolMoodle
};

export { classService };
export default classService;

// List classes with pagination and filtering
export async function listClasses(params?: ClassListParams): Promise<ClassListResponse> {
  const response = await axios.get(API_BASE, { params });
  return response.data;
}

// Get class by ID
export async function getClass(id: string): Promise<Class> {
  const response = await axios.get(`${API_BASE}/${id}`);
  return response.data;
}

// Create new class
export async function createClass(data: ClassCreateRequest): Promise<Class> {
  const response = await axios.post(API_BASE, data);
  return response.data;
}

// Update class
export async function updateClass(id: string, data: ClassUpdateRequest): Promise<{ message: string }> {
  const response = await axios.put(`${API_BASE}/${id}`, data);
  return response.data;
}

// Delete class
export async function deleteClass(id: string): Promise<{ message: string }> {
  const response = await axios.delete(`${API_BASE}/${id}`);
  return response.data;
}

// Sync classes from secondary database
export async function syncClasses(data: ClassSyncRequest): Promise<ClassSyncResponse> {
  const response = await axios.post(`${API_BASE}/sync`, data);
  return response.data;
}

// Import classes from Excel
export async function importClasses(data: ClassImportRequest): Promise<ClassImportResponse> {
  const response = await axios.post(`${API_BASE}/import`, data);
  return response.data;
}

// Download Excel template for import
export async function downloadClassTemplate(): Promise<void> {
  const templateData = [
    ['LopHocID', 'TenLop', 'KhoaHocID', 'NganhHocID'],
    ['1760212441', 'CD Y5K2A', '17602', '17'],
    ['1760222441', 'CD Y5K2B', '17602', '17'],
    ['1760232441', 'CD Y5K2C HP', '17602', '17']
  ];

  // Create CSV content
  const csvContent = templateData
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'class_template.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Helper function to parse Excel data for import
export function parseClassExcelData(jsonData: any[]): ClassCreateRequest[] {
  if (!Array.isArray(jsonData) || jsonData.length < 2) {
    throw new Error('Dữ liệu Excel không hợp lệ');
  }

  // Skip header row
  const dataRows = jsonData.slice(1);
  
  return dataRows
    .filter(row => row && row.length >= 3) // Ensure row has enough columns
    .map((row, index) => {
      try {
        return {
          LopHocID: String(row[0]).trim(),
          TenLop: String(row[1]).trim(),
          KhoaHocID: String(row[2]).trim(),
          NganhHocID: row[3] ? String(row[3]).trim() : undefined
        };
      } catch (error) {
        throw new Error(`Lỗi dữ liệu tại dòng ${index + 2}: ${error}`);
      }
    })
    .filter(item => item.LopHocID && item.TenLop && item.KhoaHocID); // Filter out invalid rows
}

export async function enrolMoodle(classId: string, moodleCourseId: number): Promise<ApiResponse<any>> {
  const response = await axios.post(`/api/v1/moodle/class/${classId}/enrol-users`, { moodleCourseId });
  return response.data;
} 